import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form } from 'formik';

import { Pagination as Pagination } from '@material-ui/lab';
import { Grid, Card, CardHeader, CardContent, Typography, Tooltip, Collapse, IconButton } from '@material-ui/core';
import MTable from '../../../../../../components/MTable';
import TableMobile from '../../../../../../components/TableMobile';
import Button from '../../../../../../components/Button';
import ModalConfirmation from '../../../../../../components/ModalConfirmation';
import Input from '../../../../../../components/Input';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretUp, faCaretDown, faTrash, faXmarkCircle, faPlusCircle } from '@fortawesome/free-solid-svg-icons';

import { loadVehicles, loadCards, registerVehicle, removeVehicle, registerCard, removeCard } from '../store/thunk';
import { toggleToRemoveVehicle, toggleToRemoveCard } from '../store/actions';

import { vehicleValidations, cardValidations } from './validations';

import useWindowDimensions from '../../../../../../helpers/hooks/useWindowDimensions';

import { useStyles } from './styles';

const vehiclesHeader = [
  { title: 'Placa', field: 'plate', sortable: false },
  { title: 'Modelo', field: 'vehicleModel', sortable: false },
  { title: 'Cor', field: 'vehicleColor', sortable: false }
];

const cardsHeader = [
  { title: 'Cartões', field: 'cardCode', sortable: false }
];

export default function VehicleRegister() {
  const dispatch = useDispatch();

  const classes = useStyles();
  const { width } = useWindowDimensions();
  const isSm = width < 960;

  const tableRowWidth = width < 768 ? '120' : '100';

  const { establishmentId } = useSelector(state => state.businessInfo);
  const userId = useSelector(state => state.profile.userId);
  const clientId = useSelector(state => state.clientRegistration.basicInfo.clientId);

  const {
    vehicles,
    cards,
    isRemovingVehicle,
    plateToRemove,
    cardToRemove,
    isRemovingCard,
    loadingVehicles,
    loadingCards
  } = useSelector(state => state.clientRegistration.vehicle);

  const vehiclesPerPage = 5;
  const vehiclePages = Math.ceil(vehicles.length / vehiclesPerPage);

  const [vehiclesList, setVehiclesList] = useState(vehicles.slice(0, vehiclesPerPage));
  const [vehiclesCurrentPage, setVehiclesCurrentPage] = useState(1);

  const [isVehicleRegistrationOpen, setIsVehicleRegistrationOpen] = useState(true);
  const [isIdRegistrationOpen, setIsIdRegistrationOpen] = useState(false);

  const initialVehicleValues = {
    plate: '',
    vehicleModel: '',
    vehicleColor: ''
  }

  const initialCardValues = {
    cardCode: ''
  }

  useEffect(() => {
    const params = {
      userId,
      establishmentId,
      clientId
    }

    dispatch(loadVehicles(params));
    dispatch(loadCards(params));
  }, []);

  useEffect(() => {
    handleLoadVehicles(vehiclesCurrentPage);
  }, [vehicles]);

  useEffect(() => {
    if(isSm) {
      setIsVehicleRegistrationOpen(true);
      setIsIdRegistrationOpen(false);
    }

    if(!isSm) {
      setIsVehicleRegistrationOpen(true);
      setIsIdRegistrationOpen(true);
    }
  }, [isSm]);

  const handleRegisterVehicle = (values, { resetForm }) => {
    const { plate, vehicleModel, vehicleColor } = values;

    const params = {
      plate: plate?.trim()?.toUpperCase(),
      vehicleModel,
      vehicleColor,
      userId,
      establishmentId,
      clientId
    }

    dispatch(registerVehicle(params));
    resetForm({ values: '' });
  }

  const onSelectVehicleToRemove = plate => {
    dispatch(toggleToRemoveVehicle(!isRemovingVehicle, plate));
  }

  const handleToggleToRemoveVehicle = () => {
    dispatch(toggleToRemoveVehicle(!isRemovingVehicle, null));
  }

  const onRemoveVehicle = () => {
    const params = {
      plate: plateToRemove,
      userId,
      establishmentId,
      clientId
    }

    dispatch(removeVehicle(params, userId));
  }

  const handleRegisterCard = (values, { resetForm }) => {
    const params = {
      cardCode: values.cardCode,
      userId,
      establishmentId,
      clientId
    }

    dispatch(registerCard(params));
    resetForm({ values: '' });
  }

  const onSelectCardToRemove = cardCode => {
    dispatch(toggleToRemoveCard(!isRemovingCard, cardCode));
  }

  const handleToggleToRemoveCard = () => {
    dispatch(toggleToRemoveCard(!isRemovingCard, null));
  }

  const onRemoveCard = () => {
    const params = {
      cardToRemove,
      userId,
      establishmentId,
      clientId
    }

    dispatch(removeCard(params));
  }

  const handleLoadVehicles = (page) => {
    setVehiclesCurrentPage(page);
    setVehiclesList(vehicles.slice((vehiclesPerPage * page) - vehiclesPerPage, vehiclesPerPage * page));
  }

  return (
    <Grid container spacing={3}>
      <Grid item md={8} xs={12}>
        <Card>
          <CardHeader
            style={{ backgroundColor: '#022A5C', padding: '8px' }}
            title={
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Typography variant="h6" className={classes.root}>
                  Cadastro de veículo
                </Typography>
                {isSm && (
                  <IconButton onClick={() => setIsVehicleRegistrationOpen(!isVehicleRegistrationOpen)}>
                    <FontAwesomeIcon
                      icon={isVehicleRegistrationOpen ? faCaretDown : faCaretUp}
                      color="#FFFFFF"
                    />
                  </IconButton>
                )}
              </div>
            }
          />
          <Collapse in={isVehicleRegistrationOpen}>
            <CardContent>
              <Grid container direction="column" spacing={1}>
                <Grid item xs={12}>
                  <Formik
                    enableReinitialize
                    initialValues={initialVehicleValues}
                    validationSchema={vehicleValidations}
                    onSubmit={handleRegisterVehicle}
                  >
                    {({ ...formikProps }) => {
                      const {
                        handleChange,
                        handleSubmit,
                        errors,
                        values: { plate, vehicleModel, vehicleColor }
                      } = formikProps;

                      return(
                        <Form>
                          <Grid container direction="row" alignItems="center" spacing={isSm ? 0 : 3}>
                            <Grid item md={3} xs={12}>
                              <Input
                                name="plate"
                                type="text"
                                label="Placa"
                                placeholder="AAA0000"
                                value={plate && plate.toUpperCase()}
                                error={errors.plate}
                                helperText={errors.plate}
                                onChange={e => {
                                  handleChange(e);
                                }}
                              />
                            </Grid>
                            <Grid item md={3} xs={12}>
                              <Input
                                type="text"
                                name="vehicleModel"
                                label="Modelo"
                                placeholder="Modelo do veículo"
                                value={vehicleModel}
                                error={errors.vehicleModel}
                                helperText={errors.vehicleModel}
                                onChange={e => {
                                  handleChange(e);
                                }}
                              />
                            </Grid>
                            <Grid item md={3} xs={12}>
                              <Input
                                type="text"
                                name="vehicleColor"
                                label="Cor"
                                placeholder="Cor do veículo"
                                value={vehicleColor}
                                error={errors.vehicleColor}
                                helperText={errors.vehicleColor}
                                onChange={e => {
                                  handleChange(e);
                                }}
                              />
                            </Grid>
                            <Tooltip
                              placement="top"
                              title="Clique no botão para adicionar o veículo"
                              enterTouchDelay={0}
                            >
                              <Grid container item md={3} xs={12} justify="flex-end">
                                <Button
                                  fullWidth={!isSm}
                                  type="submit"
                                  id="addVehicle"
                                  color="primary"
                                  loading={loadingVehicles}
                                  onClick={event => {
                                    event.preventDefault();
                                    handleSubmit();
                                  }}
                                  startIcon={
                                    <FontAwesomeIcon
                                      icon={faPlusCircle}
                                      color="#FFFFFF"
                                    />
                                  }
                                >
                                  Adicionar
                                </Button>
                              </Grid>
                            </Tooltip>
                          </Grid>
                        </Form>
                      );
                    }}
                  </Formik>
                </Grid>
                <Grid item xs={12}>
                  {isSm && (
                    <TableMobile
                      loading={loadingVehicles}
                      data={vehiclesList?.map(item => {
                        return {
                          ...item,
                          plateItem: {
                            index: 1,
                            label: 'Placa',
                            value: <Typography color='textSecondary' variant='subtitle2'>{item?.plate}</Typography>
                          },
                          vehicleModel: {
                            index: 2,
                            label: 'Modelo',
                            value: <Typography color='textSecondary' variant='subtitle2'>{item?.vehicleModel}</Typography>
                          },
                          vehicleColor: {
                            index: 3,
                            label: 'Cor',
                            value: <Typography color='textSecondary' variant='subtitle2'>{item?.vehicleColor}</Typography>
                          }
                        }
                      })}
                      actions={[
                        {
                          action: rowData => (
                            <Tooltip placement='top' title='Remover' enterTouchDelay={0}>
                              <IconButton onClick={() => onSelectVehicleToRemove(rowData?.plate)}>
                                <FontAwesomeIcon
                                  icon={faXmarkCircle}
                                  color="#F66C6B"
                                  size="xs"
                                />
                              </IconButton>
                            </Tooltip>
                          )
                        }
                      ]}
                    />
                  )}
                  {!isSm && (
                    <MTable
                      loading={loadingVehicles}
                      headers={vehiclesHeader}
                      data={vehiclesList}
                      actions={[
                        {
                          icon: () => (
                            <FontAwesomeIcon
                              icon={faTrash}
                              color="#022A5C"
                              size="xs"
                            />
                          ),
                          tooltip: 'Remover',
                          onClick: (event, rowData) => onSelectVehicleToRemove(rowData.plate)
                        }
                      ]}
                      actionColumnWidth={tableRowWidth}
                    />
                  )}
                  <Grid
                    container
                    xs={12}
                    justifyContent="flex-end"
                    style={{ marginTop: 15 }}
                  >
                    <Pagination
                      color="primary"
                      variant="outlined"
                      shape="rounded"
                      count={vehiclePages}
                      page={vehiclesCurrentPage}
                      onChange={(_, page) => handleLoadVehicles(page)}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </CardContent>
          </Collapse>
        </Card>
      </Grid>
      <Grid item md={4} xs={12}>
        <Card className="mt-4">
          <CardHeader
            style={{ backgroundColor: '#022A5C', padding: '8px' }}
            title={
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Typography variant="h6" className={classes.root}>
                  Código identificador
                </Typography>
                {isSm && (
                  <IconButton onClick={() => setIsIdRegistrationOpen(!isIdRegistrationOpen)}>
                    <FontAwesomeIcon
                      icon={isIdRegistrationOpen ? faCaretDown : faCaretUp}
                      color="#FFFFFF"
                    />
                  </IconButton>
                )}
              </div>
            }
          />
          <Collapse in={isIdRegistrationOpen}>
            <CardContent>
              <Formik
                enableReinitialize
                initialValues={initialCardValues}
                validationSchema={cardValidations}
                onSubmit={handleRegisterCard}
              >
                {({ ...formikProps }) => {
                  const {
                    handleChange,
                    handleSubmit,
                    errors,
                    values: { cardCode }
                  } = formikProps;

                  return(
                    <Form>
                      <Grid container spacing={isSm ? 0 : 3} direction="row" alignItems="center" style={{ marginBottom: isSm ? 10 : 0 }}>
                        <Grid item md={6} xs={12}>
                          <Input
                            id="cardCode"
                            name="cardCode"
                            type="number"
                            label="Cartão"
                            placeholder="000000"
                            maxLength={10}
                            value={cardCode}
                            error={errors.cardCode}
                            helperText={errors.cardCode}
                            onChange={e => {
                              handleChange(e);
                            }}
                          />
                        </Grid>
                        <Grid container item md={6} xs={12} justify="flex-end">
                          <Button
                            fullWidth={!isSm}
                            type="submit"
                            color="primary"
                            loading={loadingCards}
                            onClick={event => {
                              event.preventDefault();
                              handleSubmit();
                            }}
                            startIcon={
                              <FontAwesomeIcon
                                icon={faPlusCircle}
                                color="#FFFFFF"
                              />
                            }
                          >
                            Adicionar
                          </Button>
                        </Grid>
                      </Grid>
                    </Form>
                  );
                }}
              </Formik>
              {isSm && (
                <TableMobile
                  loading={loadingCards}
                  data={cards?.map(item => {
                    return {
                      ...item,
                      cardCodeItem: {
                        index: 1,
                        label: 'Cartão',
                        value: <Typography color='textSecondary' variant='subtitle2'>{item?.cardCode}</Typography>
                      }
                    }
                  })}
                  actions={[
                    {
                      action: rowData => (
                        <IconButton onClick={() => onSelectCardToRemove(rowData?.cardCode)}>
                          <FontAwesomeIcon
                            icon={faXmarkCircle}
                            color="#F66C6B"
                            size="xs"
                          />
                        </IconButton>
                      )
                    }
                  ]}
                />
              )}
              {!isSm && (
                <MTable
                  loading={loadingCards}
                  headers={cardsHeader}
                  data={cards}
                  actions={[
                    {
                      icon: () => (
                        <FontAwesomeIcon
                          icon={faTrash}
                          color="#022A5C"
                          size="xs"
                        />
                      ),
                      tooltip: 'Remover',
                      onClick: (event, rowData) => onSelectCardToRemove(rowData.cardCode)
                    }
                  ]}
                  actionColumnWidth={tableRowWidth}
                />
              )}
            </CardContent>
          </Collapse>
        </Card>
      </Grid>
      <ModalConfirmation
        open={isRemovingVehicle}
        isLoading={loadingVehicles}
        onClose={handleToggleToRemoveVehicle}
        handleConfirmation={onRemoveVehicle}
      >
        Deseja remover o veículo referente a placa{' '}
        <strong>{plateToRemove}</strong>?
      </ModalConfirmation>
      <ModalConfirmation
        open={isRemovingCard}
        isLoading={loadingCards}
        onClose={handleToggleToRemoveCard}
        handleConfirmation={onRemoveCard}
      >
        Deseja remover o cartão <strong>{cardToRemove}</strong>?
      </ModalConfirmation>
    </Grid>
  );
}